import { get } from 'lodash';
import moment from 'moment-timezone';

import config from '../../config';
import { LINE_ITEM_NIGHT, LINE_ITEM_LODGING_TAX, LINE_ITEM_LODGING_FEE } from '../../util/types';
import { nightsBetween, daysAdded, daysBetween } from '../../util/dates';
import {
  calculateLodgingOnlyUnitPrice,
  calculateLodgingTaxUnitPrice,
  createFlexiblePricingLineItems,
  buildEmailsMeta,
  transformLineItemsForEmail,
} from '../../util/transaction';
import {
  isGlpIncludedInTransaction,
  normalizeGlpSelection,
} from '../../util/guestLiabilityProtection';
import { getListingTimezone, getLodgingTax, listingStateLinks } from '../../util/listing';
import { types as sdkTypes } from '../../util/sdkLoader';

const { TRIP_INSURANCE_CHECKOUT_ENABLED } = require('../../shared/transaction');

const { Money } = sdkTypes;

/**
 * Builds custom pricing params for speculate/initiate transaction API calls.
 *
 * @param {Object} params Booking and listing inputs for pricing
 * @param {Object} options
 * @param {Object} options.currentUser Fallback current user when not passed in params
 * @return {Object} Params object for custom pricing bookings
 */
export const buildCustomPricingParams = (params, { currentUser: defaultCurrentUser } = {}) => {
  const protectedData = {};
  const metadata = {};

  const {
    bookingStart,
    bookingEnd,
    startLodgingDate,
    listing,
    bookingFees,
    packageItem,
    promoCode,
    tripInsurance,
    guestLiabilityProtection,
    stripePaymentIntents,
    currentUser: currentUserOverride,
    ltPlusSubscriptionId,
  } = params;

  const currentUser = currentUserOverride || defaultCurrentUser;
  const sizeRenderer = { _sdkType: 'Money', amount: 0, currency: 'USD' };

  const { anchorTag, listingState, stateStatute } = listingStateLinks(listing);
  const lodgingTaxInfo = getLodgingTax(listing, packageItem?.id);
  const landownerId = get(listing, 'author.id.uuid', null);

  const { amount, currency } =
    listing && listing.attributes && listing.attributes.price
      ? listing.attributes.price
      : sizeRenderer;

  const { party } = bookingFees;
  const unitType = config.bookingUnitType;
  const isNightly = unitType === LINE_ITEM_NIGHT;

  let quantity = isNightly
    ? nightsBetween(bookingStart, bookingEnd)
    : daysAdded(bookingStart, bookingEnd);

  if (startLodgingDate && startLodgingDate.getTime() === bookingStart.getTime()) {
    quantity -= 1;
  }

  const fees = Object.entries(bookingFees);

  const totalPartyCost = fees.reduce(function partyFunc(total, arr) {
    const correctedAmounts = arr[1]?.amount ? arr[1].amount : 0;
    return total + correctedAmounts;
  }, 0);

  // We wanna get the date at the listing location.
  // This will then be used for the display dates which are hooked
  // with our process.end transitions
  const timezone = getListingTimezone(listing);

  const bookingStartDate = moment(bookingStart).format('YYYY-MM-DD');
  const bookingEndDate = moment(bookingEnd).format('YYYY-MM-DD');
  const bookingLodgingDate = startLodgingDate
    ? moment(startLodgingDate).format('YYYY-MM-DD')
    : null;

  const bookingStartDateInLocale = moment.tz(`${bookingStartDate} 00:00:00`, timezone);
  const bookingEndDateInLocale = moment.tz(`${bookingEndDate} 23:59:59`, timezone);
  const bookingLodgingDayInLocale = bookingLodgingDate
    ? moment.tz(`${bookingLodgingDate} 00:00:00`, timezone)
    : null;

  const bookingStartDateInLocaleInUTC = moment(bookingStartDateInLocale).utc();
  const bookingEndDateInLocaleInUTC = moment(bookingEndDateInLocale).utc();

  const bookingDisplayStart = bookingStartDateInLocaleInUTC.toDate();
  const bookingDisplayEnd = bookingEndDateInLocaleInUTC.toDate();

  const totalDays =
    daysBetween(bookingStartDateInLocale.format(), bookingEndDateInLocale.format()) + 1;

  // We will store the booking dates and timezone into protected data
  // in order to access it later when displaying the dates to the guests
  // (using the listing time, NOT local guest time!!!).
  protectedData.bookingDates = {
    start: bookingStartDateInLocale.format(),
    end: bookingEndDateInLocale.format(),
    startLodgingDate: bookingLodgingDayInLocale ? bookingLodgingDayInLocale.format() : null,
    timezone,
    days: daysBetween(bookingStartDateInLocale.format(), bookingEndDateInLocale.format()) + 1,
  };

  protectedData.emailsMeta = buildEmailsMeta({
    listing,
    packageItem,
    bookingStart,
    bookingEnd,
  });

  // The API booking end date is always exclusive. In order to have e.g.
  // 1st of Jul - 2nd of July we need to send end date as 3rd.
  // Important, the 3rd of July won't be blocked from future bookings.
  const apiBookingEnd = moment
    .utc(bookingEnd)
    .set({ hour: 0, minute: 0, seconds: 0 })
    .add(1, 'd')
    .toDate();
  const apiBookingStart = moment
    .utc(bookingStart)
    .set({ hour: 0, minute: 0 })
    .toDate();

  const partyCostTransaction = (party - 1) * quantity;

  const lineItemsMaybe = fees
    .filter(([name]) => {
      // For packages we don't need that line item
      if (packageItem && name === 'party') {
        return false;
      }

      if (!partyCostTransaction && name === 'party') {
        return false;
      }

      if (packageItem && name === 'lodgingAddons') {
        return false;
      }

      if (packageItem && name === 'flexibleGuestSize') {
        return false;
      }

      return true;
    })
    .map(([name, unitPrice]) => {
      if (name === 'party') {
        return {
          code: `line-item/${name}-size`,
          unitPrice: { _sdkType: 'Money', amount: totalPartyCost, currency: 'USD' },
          quantity: partyCostTransaction,
        };
      }

      return {
        code: `line-item/${name}-fee`,
        unitPrice,
        quantity,
        guestSize: packageItem && packageItem.guestSize,
      };
    });

  if (packageItem) {
    // Check if flexible pricing is enabled and present
    const { flexibleGuestSize } = packageItem;
    const packages = get(listing, 'attributes.publicData.packages', []);
    const packageInfo = packages.find(item => item.id === packageItem.id);

    if (flexibleGuestSize && packageInfo) {
      // Use the helper function to create flexible pricing line items
      const flexiblePricingLineItems = createFlexiblePricingLineItems(
        flexibleGuestSize,
        packageItem,
        packageInfo,
        packageItem.activityDays
      );
      lineItemsMaybe.push(...flexiblePricingLineItems);
    } else {
      // Standard single package item (non-flexible pricing)
      lineItemsMaybe.push(packageItem);
    }

    protectedData.packageLineItem = {
      id: packageItem.id,
      title: packageItem.title,
      description: packageItem.description,
      price: {
        amount: packageItem.unitPrice.amount / 100,
        currency: packageItem.unitPrice.currency,
      },
      totalHuntPrice: {
        amount: packageItem.totalHuntPrice.amount / 100,
        currency: packageItem.totalHuntPrice.currency,
      },
      guestSize: packageItem.guestSize,
      activity: packageItem.activity,

      flexiblePricing: packageInfo?.flexiblePricing || undefined,
      hasLodging: packageInfo?.hasLodging || false,
      guests: packageInfo?.guests || { min: 1, max: 1 },
      days: packageInfo?.days || { min: 1, max: 365 },
      lodgingPrice: packageInfo?.lodgingPrice || undefined,
    };

    protectedData.lodgingTaxInfo = lodgingTaxInfo;
    protectedData.flexibleGuestSize = packageItem.flexibleGuestSize;
    const offeredTripInsurance = TRIP_INSURANCE_CHECKOUT_ENABLED ? tripInsurance : undefined;

    if (typeof offeredTripInsurance === 'boolean') {
      metadata.tripInsurance = offeredTripInsurance;
    }

    const glpSelection = normalizeGlpSelection(guestLiabilityProtection);
    if (glpSelection !== undefined) {
      metadata.guestLiabilityProtection = isGlpIncludedInTransaction(glpSelection);
      metadata.guestLiabilityProtectionSelection = glpSelection;
    }
  }

  // TAXES
  const lodgingTax = getLodgingTax(listing, packageItem?.id);
  const lodgingPrice = get(packageItem, 'lodgingFee', null);
  const lodgingPriceForTax = get(packageItem, 'lodgingFeeForTax', null);

  const lodgingFeeLineItemAdded = lodgingPrice && lodgingPrice.amount > 0 && totalDays > 1;
  protectedData.lodgingPrice = {};

  if (lodgingTax && lodgingPriceForTax && totalDays > 1) {
    protectedData.lodgingPrice.priceForTax = lodgingPriceForTax.amount;

    lineItemsMaybe.push({
      code: LINE_ITEM_LODGING_TAX,
      unitPrice: calculateLodgingTaxUnitPrice(lodgingTax, totalDays - 1, lodgingPriceForTax),
      quantity: 1,
      includeFor: ['customer'],
    });
  }

  if (lodgingFeeLineItemAdded) {
    protectedData.lodgingPrice.price = lodgingPrice.amount;

    const lodgingFeeUnitPrice = calculateLodgingOnlyUnitPrice(lodgingPrice, totalDays - 1);

    lineItemsMaybe.push({
      code: LINE_ITEM_LODGING_FEE,
      includeFor: ['customer', 'provider'],
      unitPrice: lodgingFeeUnitPrice,
      quantity: 1,
      lineTotal: lodgingFeeUnitPrice,
    });
  }

  if (stateStatute) {
    protectedData.stateStatute = stateStatute;
    protectedData.listingState = { anchor: anchorTag, state: listingState };
  }

  if (packageItem?.flexibleGuestSize) {
    protectedData.emailLineItems = transformLineItemsForEmail(lineItemsMaybe);
  }

  const glpSelection = normalizeGlpSelection(guestLiabilityProtection);
  const guestLiabilityProtectionForPricing = isGlpIncludedInTransaction(glpSelection);

  return {
    listingId: listing?.id,
    listingTitle: listing?.attributes?.title,
    bookingStart: apiBookingStart,
    bookingEnd: apiBookingEnd,
    bookingDisplayStart,
    bookingDisplayEnd,
    lineItems: [
      ...lineItemsMaybe,

      {
        code: unitType,
        unitPrice: new Money(amount, currency),
        quantity,
      },
    ],
    protectedData,
    metadata,
    promoCode,
    tripInsurance: TRIP_INSURANCE_CHECKOUT_ENABLED ? tripInsurance : undefined,
    guestLiabilityProtection: guestLiabilityProtectionForPricing,
    landownerId,
    currentUser,
    stripePaymentIntents,
    ltPlusSubscriptionId,
  };
};
